import { useEffect, useRef, useState } from 'react';

import { ArrowUpRightIcon, CalendarPlusIcon } from '@braskai/icons';
import { Button, Typography } from '@braskai/ui-kit';

import { CircularProgress, Stack } from '@mui/material';

import { parseBookedMeeting } from './parseBookedMeeting';

const MEETINGS_SCRIPT_SRC = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';

// If the embed can't produce a fully loaded iframe within this window, fall back to a direct link.
const SCRIPT_LOAD_TIMEOUT_MS = 15_000;

// EU host first (our CEO slug lives there); the generic host is kept for robustness.
export const HUBSPOT_MEETINGS_ORIGINS = ['https://meetings-eu1.hubspot.com', 'https://meetings.hubspot.com'] as const;

type HubSpotMeetingsProps = {
  copy?: Partial<HubSpotMeetingsCopy>;
  email?: string;
  meetingUrl: string;
  onBooked?: (startsAt?: Date) => void;
  onFallbackBooked?: () => void;
  onLoadError?: () => void;
};

export type HubSpotMeetingsCopy = {
  fallbackBookedLabel?: string;
  openBookingPage: string;
  loadErrorDescription: string;
  loadErrorTitle: string;
  loadingLabel: string;
};

const DEFAULT_COPY: HubSpotMeetingsCopy = {
  fallbackBookedLabel: 'I booked a time',
  openBookingPage: 'Open booking page',
  loadErrorDescription: 'Your network or region may be blocking the embed. Open the booking page in a new tab instead.',
  loadErrorTitle: "Couldn't load the scheduler",
  loadingLabel: 'Loading the scheduler',
};

// Module-level ref-count keyed by script src so StrictMode remounts never leave duplicate scripts/iframes.
const scriptRefCounts = new Map<string, number>();

const buildEmbedSrc = (meetingUrl: string, email?: string): string => {
  try {
    const url = new URL(meetingUrl);
    url.searchParams.set('embed', 'true');
    if (email) {
      url.searchParams.set('email', email);
    }
    return url.toString();
  } catch {
    return meetingUrl;
  }
};

/**
 * Lazily injects the HubSpot Meetings embed. Never loaded globally in index.html.
 * - Singleton, ref-counted script injection — StrictMode-safe, no duplicate iframe.
 * - Booking detected via a `message` from both the allowed origin and this embed's current iframe,
 *   gated on `meetingBookSucceeded` (current key, not the legacy `meetingCreated`) → `onBooked`.
 * - Attendee email prefilled via the `data-src` query params; a loader shows until the iframe has
 *   fully loaded (the container stays transparent meanwhile — no white-page flash).
 * - If the embed script is blocked (region/network/ad-blocker → `static.hsappstatic.net` 403/ORB) and
 *   no iframe appears, a fallback link to the booking page is shown instead of an endless spinner.
 */
export const HubSpotMeetings = ({
  copy: copyOverrides,
  email,
  meetingUrl,
  onBooked,
  onFallbackBooked,
  onLoadError,
}: HubSpotMeetingsProps) => {
  // Merge instead of replace so partial custom copy keeps the default fallback labels.
  const copy = { ...DEFAULT_COPY, ...copyOverrides };
  const containerRef = useRef<HTMLDivElement>(null);
  const [isIframeReady, setIsIframeReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasOpenedFallback, setHasOpenedFallback] = useState(false);

  // Booking detection — symmetric add/remove so the StrictMode double-invoke leaks no listeners.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!HUBSPOT_MEETINGS_ORIGINS.includes(event.origin as (typeof HUBSPOT_MEETINGS_ORIGINS)[number])) {
        return;
      }

      const iframeWindow = containerRef.current?.querySelector('iframe')?.contentWindow;

      if (!iframeWindow || event.source !== iframeWindow) {
        return;
      }

      const data = event.data as { meetingBookSucceeded?: boolean } | undefined;

      if (data?.meetingBookSucceeded) {
        onBooked?.(parseBookedMeeting(event.data));
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [onBooked]);

  // Lazy singleton script injection. Re-appending re-scans `.meetings-iframe-container` for `data-src`.
  useEffect(() => {
    const count = scriptRefCounts.get(MEETINGS_SCRIPT_SRC) ?? 0;
    scriptRefCounts.set(MEETINGS_SCRIPT_SRC, count + 1);

    let script = document.querySelector<HTMLScriptElement>(`script[src="${MEETINGS_SCRIPT_SRC}"]`);

    if (!script) {
      script = document.createElement('script');
      script.async = true;
      script.src = MEETINGS_SCRIPT_SRC;
      document.body.append(script);
    }

    // A blocked/failed script fires `error` (403, ORB, network) → show the fallback link immediately.
    const handleError = () => setHasError(true);
    script.addEventListener('error', handleError);

    return () => {
      script?.removeEventListener('error', handleError);

      const next = (scriptRefCounts.get(MEETINGS_SCRIPT_SRC) ?? 1) - 1;
      scriptRefCounts.set(MEETINGS_SCRIPT_SRC, Math.max(0, next));

      if (next <= 0) {
        document.querySelector(`script[src="${MEETINGS_SCRIPT_SRC}"]`)?.remove();
      }
    };
  }, []);

  // Hide the loader only when the iframe has fully LOADED (not just been inserted) — otherwise
  // the spinner disappears while the iframe is still a white page.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let watched: HTMLIFrameElement | null = null;
    const markReady = () => setIsIframeReady(true);

    const watch = (iframe: HTMLIFrameElement) => {
      watched?.removeEventListener('load', markReady);
      watched = iframe;
      iframe.addEventListener('load', markReady);
    };

    const existing = container.querySelector('iframe');
    if (existing) {
      watch(existing);
    }

    const observer = new MutationObserver(() => {
      const iframe = container.querySelector('iframe');
      if (iframe && iframe !== watched) {
        watch(iframe);
      }
    });

    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      watched?.removeEventListener('load', markReady);
    };
  }, []);

  // Fallback timeout for a script that "loads" but never builds an iframe; cleared once the iframe shows.
  useEffect(() => {
    if (isIframeReady) {
      return;
    }

    const timer = window.setTimeout(() => setHasError(true), SCRIPT_LOAD_TIMEOUT_MS);

    return () => window.clearTimeout(timer);
  }, [isIframeReady]);

  const showFallback = hasError && !isIframeReady;

  // Report the embed failure exactly once (lets the funnel track how many users can't load the embed).
  const errorReportedRef = useRef(false);
  useEffect(() => {
    if (showFallback && !errorReportedRef.current) {
      errorReportedRef.current = true;
      onLoadError?.();
    }
  }, [showFallback, onLoadError]);

  return (
    <>
      {!isIframeReady && !hasError && (
        <CircularProgress aria-label={copy.loadingLabel} sx={{ display: 'block', mx: 'auto', my: 4 }} />
      )}

      {showFallback && (
        <Stack alignItems="center" gap={16} sx={{ px: 16, py: 32, textAlign: 'center' }}>
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ bgcolor: 'grey.100', borderRadius: '50%', color: 'grey.700', height: 48, width: 48 }}
          >
            <CalendarPlusIcon />
          </Stack>

          <Stack alignItems="center" gap={4}>
            <Typography color="grey.900" variant="title-4-md">
              {copy.loadErrorTitle}
            </Typography>
            <Typography color="grey.500" maxWidth={360} variant="body-1">
              {copy.loadErrorDescription}
            </Typography>
          </Stack>

          <Button
            data-testid="hub-spot-meetings-open-booking-page"
            endIcon={<ArrowUpRightIcon />}
            size="lg"
            variant="primary"
            onClick={() => {
              window.open(meetingUrl, '_blank', 'noopener,noreferrer');
              setHasOpenedFallback(true);
            }}
          >
            {copy.openBookingPage}
          </Button>

          {hasOpenedFallback && onFallbackBooked && copy.fallbackBookedLabel && (
            <Button
              data-testid="hub-spot-meetings-confirm-fallback-booking"
              size="md"
              variant="outlined"
              onClick={onFallbackBooked}
            >
              {copy.fallbackBookedLabel}
            </Button>
          )}
        </Stack>
      )}

      <div
        className="meetings-iframe-container"
        data-src={buildEmbedSrc(meetingUrl, email)}
        ref={containerRef}
        style={{ opacity: isIframeReady ? 1 : 0, transition: 'opacity 0.25s ease' }}
      />
    </>
  );
};
